import React from 'react';
import "/opt/build/repo/src/common/fare-programs.tsx?resplendence=true";
import { useMapView } from 'common/use-map-view';
import useData from 'utils/use-data';
import { gql } from '@apollo/client';
import {
    FareProgram,
    MapViewFareProgramsArgs,
    MapViewFareProgramsData
} from 'graphql.g';
import { useQueryState } from 'router';
import { Select } from 'common/select';
import {
    components as reactSelectComponents,
    OptionProps,
    SingleValueProps
} from 'react-select';
import { defineMessages, IntlShape, useIntl } from 'react-intl';
import * as strings from 'strings';
import { orderBy } from 'lodash';

/*
@use 'style' as *;
*/;

const MAPVIEW_QUERY = gql`
    query MapViewFarePrograms($slug: String!) {
        mapView(slug: $slug) {
            id
            availableFarePrograms
        }
    }
`;

export function useFarePrograms() {
    const { slug } = useMapView();
    const intl = useIntl();

    const mapViewData = useData<
        MapViewFareProgramsData,
        MapViewFareProgramsArgs
    >(MAPVIEW_QUERY, { slug });

    if (!mapViewData) return null;

    return orderBy(mapViewData?.mapView.availableFarePrograms ?? [], fp =>
        intl.formatMessage(strings.farePrograms[fp])
    );
}

const messages = defineMessages({
    'all-fare-programs-option': 'All fare programs ({count})'
});

export function useFareProgram(searchParam: string = 'fareProgram') {
    const [fareProgram] = useQueryState<FareProgram | null>(searchParam, {
        constrain: {
            options: new Set([null, ...Object.values(FareProgram)]),
            fallbackValue: null
        }
    });
    return fareProgram;
}

const getFareProgramOption =
    (intl: IntlShape) =>
    (fp: FareProgram): FareProgramOption => {
        return {
            value: fp,
            label: intl.formatMessage(strings.farePrograms[fp])
        };
    };

interface FareProgramPickerProps {
    farePrograms: FareProgram[];
    onChange: (fp: FareProgram | null) => void;
    value: FareProgram | null;
}

/** A fare program selector that requires the state to be manage by the component
 * rendering this selector.
 */
export function FareProgramPicker({
    farePrograms,
    onChange,
    value
}: FareProgramPickerProps) {
    const intl = useIntl();

    const allFareProgramsOption = {
        value: null,
        label: intl.formatMessage(messages['all-fare-programs-option'], {
            count: farePrograms.length
        }),
        icon: null
    };

    const fareProgramOptions = [
        allFareProgramsOption,
        ...farePrograms.map(getFareProgramOption(intl))
    ];

    return (
        <Select
            onChange={option => onChange(option?.value ?? null)}
            value={
                value != null
                    ? getFareProgramOption(intl)(value)
                    : allFareProgramsOption
            }
            options={fareProgramOptions}
            components={{
                Option: Option,
                SingleValue: SingleValue
            }}
            styles={{
                option: (base, { isDisabled }) => ({
                    ...base,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    cursor: isDisabled ? 'default' : 'pointer'
                }),
                singleValue: base => ({
                    ...base,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start'
                })
            }}
        />
    );
}

interface FareProgramQueryStatePickerProps {
    farePrograms: FareProgram[];
    searchParam?: string;
}

/** A fare program selector that stores its state in the query params */
export function FareProgramQueryStatePicker({
    farePrograms,
    searchParam = 'fareProgram'
}: FareProgramQueryStatePickerProps) {
    const [fareProgram, setFareProgram] = useQueryState(searchParam, {
        constrain: {
            options: new Set([...farePrograms, null]),
            fallbackValue: null
        }
    });

    return (
        <FareProgramPicker
            farePrograms={farePrograms}
            onChange={setFareProgram}
            value={fareProgram}
        />
    );
}

interface FareProgramOption {
    value: FareProgram | null;
    label: string;
}

function Option(props: OptionProps<FareProgramOption, false>) {
    return (
        <reactSelectComponents.Option {...props}>
            {props.data.label}
        </reactSelectComponents.Option>
    );
}

function SingleValue(props: SingleValueProps<FareProgramOption, false>) {
    return (
        <reactSelectComponents.SingleValue {...props}>
            {props.data.label}
        </reactSelectComponents.SingleValue>
    );
}
